import { useState, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  VStack,
  Text,
  Tag,
  HStack,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { MagnifyingGlass, CaretDown, X } from '@phosphor-icons/react';

export default function SearchableMultiSelectDropdown({
  options, // Array of { value: string, label: string }
  selectedOptions, // Array of string values
  onChange, // (value: string, isSelected: boolean) => void
  placeholder = "Select options",
  searchPlaceholder = "Search...",
  isDisabled = false,
  variant = "default" // "default" or "map"
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);

  const menuBg = useColorModeValue(variant === 'map' ? 'ancient.papyrus' : 'bg.surface', variant === 'map' ? 'gray.700' : 'bg.surface');
  const menuBorderColor = useColorModeValue(variant === 'map' ? 'ancient.mapBorder' : 'border.default', variant === 'map' ? 'ancient.mapBorder' : 'border.default');
  const itemHoverBg = useColorModeValue(variant === 'map' ? 'ancient.lightStone' : 'bg.accent', variant === 'map' ? 'gray.600' : 'bg.accent');
  const textColor = useColorModeValue(variant === 'map' ? 'ancient.ink' : 'text.default', variant === 'map' ? 'ancient.parchment' : 'text.default');
  const inputBg = useColorModeValue(variant === 'map' ? 'ancient.parchment' : 'bg.surface', variant === 'map' ? 'gray.600' : 'bg.surface');
  const tagBg = useColorModeValue(variant === 'map' ? 'ancient.terracotta' : 'interactive.primary', variant === 'map' ? 'ancient.gold' : 'interactive.primary');
  const tagColor = useColorModeValue(variant === 'map' ? 'white' : 'white', variant === 'map' ? 'ancient.ink' : 'white');
  const buttonBorderColor = useColorModeValue(variant === 'map' ? 'ancient.mapBorder' : 'border.default', variant === 'map' ? 'ancient.mapBorder' : 'border.default');


  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const handleMenuToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Focus input when opening
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearchTerm(''); // Clear search on close
    }
  };
  
  const getDisplayValue = () => {
    if (selectedOptions.length === 0) {
      return <Text color={textColor} opacity={0.7}>{placeholder}</Text>;
    }
    if (selectedOptions.length <= 2) {
      return selectedOptions.map(value => {
        const option = options.find(opt => opt.value === value);
        return (
          <Tag key={value} size="sm" borderRadius="full" variant="solid" bg={tagBg} color={tagColor} mr={1}>
            {option?.label || value}
          </Tag>
        );
      });
    }
    return (
      <Tag size="sm" borderRadius="full" variant="solid" bg={tagBg} color={tagColor}>
        {selectedOptions.length} selected
      </Tag>
    );
  };

  return (
    <Menu isOpen={isOpen} onClose={() => { setIsOpen(false); setSearchTerm(''); }} closeOnSelect={false}>
      <MenuButton
        as={Button}
        variant={variant === 'map' ? 'map' : 'ghost'}
        size="sm"
        rightIcon={<Icon as={CaretDown} color={variant === 'map' ? 'ancient.terracotta' : 'gray.400'} />}
        onClick={handleMenuToggle}
        isDisabled={isDisabled}
        width="100%"
        textAlign="left"
        fontWeight="normal"
        bg={variant === 'map' ? 'ancient.papyrus' : useColorModeValue('bg.surface', 'bg.surface')}
        color={textColor}
        borderWidth="1px"
        borderStyle="solid"
        borderColor={buttonBorderColor}
        borderRadius="md"
        _hover={{ 
          borderColor: useColorModeValue(variant === 'map' ? 'ancient.gold' : 'interactive.primary', variant === 'map' ? 'ancient.goldHover' : 'interactive.primary'),
          bg: useColorModeValue(variant === 'map' ? 'ancient.lightStone' : 'bg.accent', variant === 'map' ? 'gray.600' : 'bg.accent')
        }}
        _active={{ 
          bg: useColorModeValue(variant === 'map' ? 'ancient.lightStone' : 'bg.accent', variant === 'map' ? 'gray.600' : 'bg.accent'),
          borderColor: useColorModeValue(variant === 'map' ? 'ancient.gold' : 'interactive.primary', variant === 'map' ? 'ancient.goldHover' : 'interactive.primary')
        }}
        _focus={{ 
          borderColor: useColorModeValue(variant === 'map' ? 'ancient.gold' : 'interactive.primary', variant === 'map' ? 'ancient.goldHover' : 'interactive.primary'),
          boxShadow: `0 0 0 1px ${useColorModeValue(variant === 'map' ? 'var(--chakra-colors-ancient-gold)' : 'var(--chakra-colors-interactive-primary)', variant === 'map' ? 'var(--chakra-colors-ancient-goldHover)' : 'var(--chakra-colors-interactive-primary)')}`
        }}
      >
        <HStack spacing={1} overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
         {getDisplayValue()}
        </HStack>
      </MenuButton>
      <MenuList 
        p={2} 
        zIndex={1500} 
        bg={menuBg} 
        borderColor={menuBorderColor}
        boxShadow="lg"
        minW="250px"
        maxW="350px"
        position="relative"
      >
        <InputGroup size="sm" mb={2}>
          <InputLeftElement pointerEvents="none">
            <Icon as={MagnifyingGlass} color={variant === 'map' ? 'ancient.terracotta' : "gray.400"} />
          </InputLeftElement>
          <Input
            ref={inputRef}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent menu from closing
            size="sm"
            borderRadius="md"
            bg={inputBg}
            borderColor={buttonBorderColor}
            color={textColor}
            _focus={{ borderColor: useColorModeValue(variant === 'map' ? 'ancient.gold' : 'interactive.primary', variant === 'map' ? 'ancient.goldHover' : 'interactive.primary'), boxShadow: `0 0 0 1px ${useColorModeValue(variant === 'map' ? 'var(--chakra-colors-ancient-gold)' : 'var(--chakra-colors-interactive-primary)', variant === 'map' ? 'var(--chakra-colors-ancient-goldHover)' : 'var(--chakra-colors-interactive-primary)')}` }}
          />
        </InputGroup>
        <VStack spacing={1} align="stretch" maxHeight="200px" overflowY="auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map(option => (
              <MenuItem
                key={option.value}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent menu from closing
                  onChange(option.value, !selectedOptions.includes(option.value));
                }}
                closeOnSelect={false}
                bg="transparent"
                _hover={{ bg: itemHoverBg }}
                borderRadius="md"
                p={0} // Remove default MenuItem padding
              >
                <Checkbox
                  isChecked={selectedOptions.includes(option.value)}
                  isReadOnly // Let MenuItem handle click
                  pointerEvents="none" // Ensure click goes to MenuItem
                  width="100%"
                  p={2} // Add padding to checkbox container for better click area
                  color={textColor}
                  sx={{
                    '& .chakra-checkbox__control': {
                      borderColor: useColorModeValue('border.default', 'border.default'),
                      _checked: {
                        bg: useColorModeValue('interactive.primary', 'interactive.primary'),
                        borderColor: useColorModeValue('interactive.primary', 'interactive.primary'),
                        _hover: {
                          bg: useColorModeValue('interactive.hover', 'interactive.hover'),
                          borderColor: useColorModeValue('interactive.hover', 'interactive.hover'),
                        }
                      },
                      _hover: {
                        borderColor: useColorModeValue('interactive.primary', 'interactive.primary'),
                      }
                    }
                  }}
                >
                  <Text fontSize="sm" color={textColor}>{option.label}</Text>
                </Checkbox>
              </MenuItem>
            ))
          ) : (
            <Text textAlign="center" p={2} fontSize="sm" color={textColor} opacity={0.7}>
              No options found.
            </Text>
          )}
        </VStack>
      </MenuList>
    </Menu>
  );
}
